/**
 * The two `/agents/workflows/new` builders — RO-3-02b.
 *
 * Port of `_builder.tsx` (277 LOC, the conversational/guided builder) and
 * `_wizard.tsx` (83, the quick form). Both are unchanged except that their three
 * server actions are now `apiCall`s against `workflowBuilder`, and `useRouter()`
 * is the router seam's `useNavigate()`.
 *
 * The presets and copy come from `@/optimization/builder-shared` — the SAME pure
 * module the original imported and the server proposal engine re-exports. That
 * module exists precisely so both sides of the boundary share one definition
 * (its header explains the `node:fs` hazard it was split out of), so importing
 * it through the `@/` alias here is reuse, not a fork.
 */

import * as React from "react";
import {
  AskUserQuestions,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  Icon,
  Input,
  ObjectiveEditor,
  type AskUserAnswer,
  type AskUserQuestion,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import { useNavigate } from "@excellent/ui/router";
import { workflowBuilder } from "@excellent/ui/api/contracts/workflows/builder";
import {
  GATE_POLICY_COPY,
  OBJECTIVE_PRESETS,
  type GatePolicy,
  type LoopProposal,
  type ObjectivePreset,
} from "@/optimization/builder-shared";

export function NewWorkflowBuilders({ mode }: { mode: "form" | "guided" }) {
  return mode === "form" ? <NewWorkflowForm /> : <GuidedWorkflowBuilder />;
}

// ── The guided builder (port of `_builder.tsx`) ───────────────────────────────

/** Quick-start descriptions for the first question's options. */
const QUICK_STARTS: Record<string, string> = {
  support: "Triage inbound support tickets and draft replies",
  sales: "Draft and personalize outbound sales emails per segment",
  code: "Ship a code change; a different agent verifies it",
  content: "Outline, draft, and edit a piece of marketing content",
};

const DESCRIBE_Q: AskUserQuestion = {
  id: "describe",
  title: "What should this workflow do?",
  hint: "Describe it in a sentence — or pick a starting point. You can tweak everything next.",
  skippable: false,
  allowOther: true,
  otherPlaceholder: "e.g. Triage inbound support emails and draft a reply for each…",
  layout: "stacked",
  options: [
    { id: "support", title: "Support triage", description: "Classify + reply to inbound support" },
    { id: "sales", title: "Sales email", description: "Draft + personalize outbound" },
    { id: "code", title: "Code review", description: "Ship + verify a change" },
    { id: "content", title: "Content draft", description: "Outline → draft → edit" },
  ],
};

type Phase = "describe" | "thinking" | "configure";

function GuidedWorkflowBuilder() {
  const navigate = useNavigate();
  const [phase, setPhase] = React.useState<Phase>("describe");
  const [proposal, setProposal] = React.useState<LoopProposal | null>(null);
  const [title, setTitle] = React.useState("");
  const [domain, setDomain] = React.useState("");
  const [answers, setAnswers] = React.useState<Record<string, AskUserAnswer>>({});
  const [err, setErr] = React.useState<string | null>(null);
  const [proposing, setProposing] = React.useState(false);
  const [creating, setCreating] = React.useState(false);

  // ── Phase 1: describe → propose ────────────────────────────────────────────
  const onDescribed = (a: Record<string, AskUserAnswer>) => {
    const ans = a["describe"];
    const text = ans?.otherText?.trim() || QUICK_STARTS[ans?.selectedIds?.[0] ?? ""] || "";
    if (!text) {
      setErr("Describe the workflow, or pick a starting point.");
      return;
    }
    setErr(null);
    setPhase("thinking");
    setProposing(true);
    void (async () => {
      try {
        const r = await apiCall(workflowBuilder.propose, { body: { description: text } });
        if (!r.ok) {
          setErr(r.error);
          setPhase("describe");
          return;
        }
        // The wire shape is structurally `LoopProposal`; the contract mirrors it
        // field for field (see the contract's header on why it cannot import it).
        const p = r.proposal as LoopProposal;
        setProposal(p);
        setTitle(p.title);
        setDomain(p.domain);
        // Seed the configure answers from the proposal (controlled → live preview).
        setAnswers({
          objective: { questionId: "objective", selectedIds: [p.objectivePreset] },
          gate: { questionId: "gate", selectedIds: [p.gate_policy] },
          steps: { questionId: "steps", selectedIds: p.steps.map((s) => s.name) },
          activate: { questionId: "activate", selectedIds: ["draft"] },
        });
        setPhase("configure");
      } finally {
        setProposing(false);
      }
    })();
  };

  // ── Phase 2: configure → create ────────────────────────────────────────────
  const configureQuestions = (p: LoopProposal): AskUserQuestion[] => [
    {
      id: "objective",
      title: "What should it optimize for?",
      layout: "stacked",
      skippable: false,
      options: (Object.keys(OBJECTIVE_PRESETS) as ObjectivePreset[]).map((k) => ({
        id: k,
        title: OBJECTIVE_PRESETS[k].label,
        description: OBJECTIVE_PRESETS[k].description,
      })),
    },
    {
      id: "gate",
      title: "How should changes be approved?",
      layout: "stacked",
      skippable: false,
      options: (["ship-verify", "human", "auto-eval"] as GatePolicy[]).map((g) => ({
        id: g,
        title: g,
        description: GATE_POLICY_COPY[g],
      })),
    },
    {
      id: "steps",
      title: "Confirm the steps",
      hint: "These become the workflow's pipeline. Deselect any you don't want, or add your own.",
      multiSelect: true,
      skippable: false,
      allowOther: true,
      otherPlaceholder: "add a step (e.g. summarize)…",
      options: p.steps.map((s) => ({
        id: s.name,
        title: s.name,
        description: s.role ?? undefined,
      })),
    },
    {
      id: "activate",
      title: "Turn it on now?",
      layout: "stacked",
      skippable: false,
      options: [
        {
          id: "draft",
          title: "Keep as draft",
          description: "Off until you activate it (recommended)",
        },
        {
          id: "activate",
          title: "Activate now",
          description: "Start running + optimizing immediately",
        },
      ],
    },
  ];

  const onConfigured = (a: Record<string, AskUserAnswer>) => {
    if (!proposal) return;
    setErr(null);
    const preset =
      (a["objective"]?.selectedIds?.[0] as ObjectivePreset) ?? proposal.objectivePreset;
    const gate = (a["gate"]?.selectedIds?.[0] as GatePolicy) ?? proposal.gate_policy;
    const keptNames = a["steps"]?.selectedIds ?? proposal.steps.map((s) => s.name);
    const custom = a["steps"]?.otherText?.trim();
    const steps = proposal.steps
      .filter((s) => keptNames.includes(s.name))
      .map((s) => ({ name: s.name, role: s.role, rung: s.rung ?? 1 }));
    if (custom) steps.push({ name: custom, role: undefined, rung: 1 });
    if (steps.length === 0) {
      setErr("A workflow needs at least one step.");
      return;
    }
    const activate = a["activate"]?.selectedIds?.[0] === "activate";

    setCreating(true);
    void (async () => {
      try {
        const r = await apiCall(workflowBuilder.createFromBuilder, {
          body: {
            title: title.trim() || proposal.title,
            domain: domain.trim() || proposal.domain,
            description: proposal.description,
            objectivePreset: preset,
            objective: OBJECTIVE_PRESETS[preset].objective,
            gate_policy: gate,
            steps,
            activate,
          },
        });
        if (r.ok && r.slug) navigate.push(`/agents/workflows/${r.slug}`);
        else setErr(r.error ?? "Create failed.");
      } finally {
        setCreating(false);
      }
    })();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (phase === "describe") {
    return (
      <div className="flex flex-col gap-3">
        <AskUserQuestions questions={[DESCRIBE_Q]} onComplete={onDescribed} />
        {err && <p className="type-body text-destructive">{err}</p>}
      </div>
    );
  }

  if (phase === "thinking" || proposing || !proposal) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 py-8 type-body text-muted-foreground">
          <Icon name="loader-2" size="sm" className="animate-spin" />
          Designing your workflow…
        </CardContent>
      </Card>
    );
  }

  const livePreset =
    (answers["objective"]?.selectedIds?.[0] as ObjectivePreset) ?? proposal.objectivePreset;
  const liveGate = (answers["gate"]?.selectedIds?.[0] as GatePolicy) ?? proposal.gate_policy;
  const liveSteps = answers["steps"]?.selectedIds ?? proposal.steps.map((s) => s.name);
  const liveCustom = answers["steps"]?.otherText?.trim();

  return (
    <div className="flex flex-col gap-4">
      {/* Live preview of the workflow being assembled */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon name="git-branch" size="sm" className="text-muted-foreground" />
            Your workflow
            {proposal.source === "llm-refined" && (
              <Badge variant="secondary" className="ml-1">
                refined by Claude
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Title" htmlFor="builder-title">
              <Input
                id="builder-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormField>
            <FormField label="Domain" htmlFor="builder-domain">
              <Input
                id="builder-domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
              />
            </FormField>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="type-subtext text-muted-foreground">Pipeline</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {liveSteps.length === 0 && !liveCustom && (
                <span className="type-body text-muted-foreground">no steps selected</span>
              )}
              {liveSteps.map((s, i) => (
                <span key={s} className="flex items-center gap-1.5">
                  {i > 0 && <Icon name="arrow-right" size="xs" className="text-muted-foreground" />}
                  <Badge variant="outline">{s}</Badge>
                </span>
              ))}
              {liveCustom && (
                <span className="flex items-center gap-1.5">
                  <Icon name="arrow-right" size="xs" className="text-muted-foreground" />
                  <Badge variant="outline">{liveCustom}</Badge>
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 type-subtext text-muted-foreground">
            <span>
              Objective ·{" "}
              <span className="text-foreground">{OBJECTIVE_PRESETS[livePreset].label}</span>
            </span>
            <span>
              Gate · <span className="font-mono text-foreground">{liveGate}</span>
            </span>
          </div>
        </CardContent>
      </Card>

      <AskUserQuestions
        questions={configureQuestions(proposal)}
        answers={answers}
        onAnswersChange={setAnswers}
        onComplete={onConfigured}
      />

      {creating && (
        <p className="flex items-center gap-2 type-body text-muted-foreground">
          <Icon name="loader-2" size="sm" className="animate-spin" /> Creating workflow…
        </p>
      )}
      {err && <p className="type-body text-destructive">{err}</p>}
    </div>
  );
}

// ── The quick form (port of `_wizard.tsx`) ───────────────────────────────────

/**
 * Identity → Objective → Gate → create. One card per concern, as before.
 *
 * The gate picker was three hand-rolled `<label><input type="radio">` rows.
 * Those are the one thing NOT copied verbatim: CLAUDE.md rule 12 says fields are
 * presented one way (label-led, through `FormField`), and a hand-rolled `<label>`
 * is named there as the anti-pattern. The three options and their copy are
 * unchanged — only the presentation moves onto the system's own field.
 */
function NewWorkflowForm() {
  const navigate = useNavigate();
  const [pending, setPending] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [domain, setDomain] = React.useState("support");
  const [description, setDescription] = React.useState("");
  const [objective, setObjective] = React.useState(
    JSON.stringify({
      correctness: { weight: 1, floor: 1.0 },
      cost_usd: { weight: 0.5, minimize: true },
    }),
  );
  const [gate, setGate] = React.useState<GatePolicy>("ship-verify");
  const [err, setErr] = React.useState<string | null>(null);

  const create = (activate: boolean) => {
    setErr(null);
    if (!title.trim()) {
      setErr("Title is required.");
      return;
    }
    setPending(true);
    void (async () => {
      try {
        const r = await apiCall(workflowBuilder.create, {
          body: { title, domain, description, objective, gate_policy: gate, activate },
        });
        if (r.ok && r.slug) navigate.push(`/agents/workflows/${r.slug}`);
        else setErr(r.error ?? "create failed");
      } finally {
        setPending(false);
      }
    })();
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <FormField label="Title" required htmlFor="wf-title">
            <Input
              id="wf-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Support triage"
            />
          </FormField>
          <FormField label="Domain" htmlFor="wf-domain">
            <Input
              id="wf-domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="code | support | sales | marketing"
            />
          </FormField>
          <FormField label="Description" htmlFor="wf-description">
            <Input
              id="wf-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormField>
        </CardContent>
      </Card>

      <ObjectiveEditor
        initial={objective}
        onSave={(json) => setObjective(json)}
        saveLabel="Use this objective"
      />

      <Card>
        <CardHeader>
          <CardTitle>Gate policy</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {(["ship-verify", "human", "auto-eval"] as GatePolicy[]).map((g) => (
            <Button
              key={g}
              variant={gate === g ? "default" : "outline"}
              onClick={() => setGate(g)}
              aria-pressed={gate === g}
              className="justify-start gap-2"
            >
              <span className="font-mono">{g}</span>
              <span className="type-subtext opacity-80">{GATE_POLICY_COPY[g]}</span>
            </Button>
          ))}
        </CardContent>
      </Card>

      {err && <p className="type-body text-destructive">{err}</p>}
      <div className="flex items-center gap-3">
        <Button disabled={pending} onClick={() => create(false)}>
          {pending ? "Creating…" : "Create workflow (draft)"}
        </Button>
        <Button variant="outline" disabled={pending} onClick={() => create(true)}>
          Create &amp; activate
        </Button>
      </div>
    </div>
  );
}
